<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  GeneralLedger,
  Item,
  RecordStatus,
  Warehouse,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDFBlob,
  formatDate,
  isEmpty,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";

import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemStockGrid from "../../../../components/ui/Templates/ItemStockGrid.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import GridInput from "../../../../components/ui/form/GridInput.vue";
import Buttons from '../../../../components/ui/form/Buttons.vue'
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import AccountingLog from "../../../../components/ui/Templates/AccountingLog.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  id: props.data?.id || "",
  stock_adjustment_types_id: props.data?.stock_adjustment_types_id || "",
  adjustment_account_id: props.data?.adjustment_account_id || "",
  warehouses_id: props.data?.warehouses_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  currency: props.data?.currency || "MVR",
  currencys_id: props.data?.currencys_id || 1,
  exchange_rate: props.data?.exchange_rate || 1,
  prefix: props.data?.prefix || "",
  ref_no: props.data?.ref_no || "",
  attachment: props.data?.attachment || "",
  counted_users_id: props.data?.counted_users_id || "",
  approved_users_id: props.data?.approved_users_id || "",
  accounting: props.data?.accounting || ([] as GeneralLedger[]),
  uid: props.data?.uid || "",
  status: props.data?.status || "Pending",
  document_no: props.data?.document_no || "",
  items: [] as any,
  download: false,
});

const warehouse = ref<Warehouse>();

//setFieldRules("outlets_id", [required("Select Outlet")]);
setFieldRules("stock_adjustment_types_id", [required("Select Adjustment Type")]);
setFieldRules("adjustment_account_id", [required("Select Adjustment Account")]);
setFieldRules("warehouses_id", [required("Select Warhouse")]);
setFieldRules("counted_users_id", [required("Counted User is required")]);
setFieldRules("items", [required("Select at least 1 item")]);

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
  }
};

const validateGrid = async (): Promise<boolean> => {
  for (const item of formData.value.items) {
    if (item.tracked_by === "Batch") {
      const batch_qty = item.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );
      if (batch_qty !== item.inventory_qty) {
        return false;
      }
    } else if (item.tracked_by === "Serial") {
      const serial_qty = item.serials?.reduce(
        (sum: number, serial: { quantity: number }) => {
          if (typeof serial.quantity === "number") {
            return sum + serial.quantity;
          }
          return sum;
        },
        0
      );
      if (serial_qty !== item.inventory_qty) {
        return false;
      }
    }
  }
  return true;
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validateGrid();
    if (!isValid) {
      MessageBox.show(
        "There are some Batch/Serial Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      fields.value.items.error = "Select at least 1 item";
      //MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    formData.value.download = download || share || print;
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDFBlob(response.data.file, 'Stock Adjustment #: ' + response.data.data.document_no);
      } else if (mail) {
        if (!response.data.error) {
          Toast.show('Stock Adjustment #: ' + response.data.data.document_no + " emailed successfully");
        }
      } else if (share) {
        sharePDFBlob(
          response.data.file, 'Stock Adjustment #: ' + response.data.data.document_no
        );
      } else if (print) {
        printPDFBlob(response.data.file);
      }
      setFormData(response.data.data);
    }
  }
};

const clearForm = () => {
  resetForm();

  fields.value.date_time.value = formatDate(new Date());
  fields.value.warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  isReadOnly.value = false;

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const allowNewItemRow = ref(false);

const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.warehouses_id) &&
    !isEmpty(formData.value.stock_adjustment_types_id) &&
    !isEmpty(formData.value.adjustment_account_id)
  ) {
    if (props.cartItems) prepareCartItems();
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const prepareCartItems = async () => {
  const ids = props.cartItems?.map((item) => item.id);
  const response = await api.post(
    "clients/inventory/item-management/items/items",
    {
      ids: ids,
      warehouses_id: formData.value.warehouses_id,
    }
  );
  if (response.success == true) {
    if (itemGridRef.value) {
      itemGridRef.value.handleSelectedItems(response.data);
    }
  }
};

const postStockAdjustment = async () => {
  const response = await api.post(
    props.endPoint + "approve-adjustment",
    {
      id: fields.value.uid.value,
    }
  );
  if (response.success == true) {
    setFormData(response.data);
    Toast.show("Stock Adjustment approved successfully");
  }
};

const approveStockAdjustment = async () => {
  MessageBox.confirm({
        title: 'Confirm',
        message: 'Are you sure you want to Approve this Stock Adjustment? <br /> <strong>Note:</strong> This action cannot be undone.</strong>', 
        confirmText: 'Yes, Approve',
        cancelText: "Cancel",
      }).then((confirmed: any) => {
        if (confirmed) {
          postStockAdjustment();
        }
      });
};

const activeTab = ref("items");

const selectAdjustmentType = (data: any) => {
  if (data) {
    fields.value.adjustment_account_id.value = Number(data.adjustment_account_id);
    prepareItemRow();
  } else {
    formData.value.adjustment_account_id = "";
    prepareItemRow();
  }
};
const itemGridRef = ref<InstanceType<typeof ItemStockGrid> | null>(null);

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Stock Adjustment# " + formData.value.ref_no);
};

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});
</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title + (formData.document_no ? ' - ' + formData.document_no : '')"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="false"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
        <template #read-buttons>
          <Buttons caption="Approve" 
          @click="approveStockAdjustment"
          v-if="formData.status == 'Pending' && isReadOnly"
          icon="Check" 
          type="info" />

        </template>
      </DocumentHeader>
    </template>
    <!-- Main Content - Scrollable -->
    <template #form>
      <!--header-->
      <div class="grid md:grid-cols-5 grid-cols-2 gap-6 mb-8">
        <SearchSelect
          label="Warehouse"
          end-point="system/company/warehouses"
          v-model="fields.warehouses_id.value"
          :readonly="isReadOnly"
          @select="prepareItemRow"
          :error="
            fields.warehouses_id.touched ? fields.warehouses_id.error : null
          "
          @blur="fields.warehouses_id.touched = true"
        />
        <SearchSelect
          label="Adjustment Type"
          end-point="inventory/inventory-management/stock-adjustment-types"
          v-model="fields.stock_adjustment_types_id.value"
          :readonly="isReadOnly"
          @select="selectAdjustmentType"
          :error="fields.stock_adjustment_types_id.touched ? fields.stock_adjustment_types_id.error : null"
          @blur="fields.stock_adjustment_types_id.touched = true"
        />
        <SearchSelect
          label="Adjustment Account"
          end-point="finance/accounting/chart-of-accounts"
          extra="Expenses"
          v-model="fields.adjustment_account_id.value"
          :readonly="isReadOnly"
          @select="prepareItemRow"
          :error="
            fields.adjustment_account_id.touched ? fields.adjustment_account_id.error : null
          "
          @blur="fields.adjustment_account_id.touched = true"
        />
        <SearchSelect
          label="Counted User"
          end-point="system/security/users"
          v-model="fields.counted_users_id.value"
          :readonly="isReadOnly"
          :error="
            fields.counted_users_id.touched ? fields.counted_users_id.error : null
          "
          @blur="fields.counted_users_id.touched = true"
        />
        <SearchSelect
          label="Approved User"
          end-point="system/security/users"
          v-model="fields.approved_users_id.value"
          :readonly="isReadOnly"
          :error="
            fields.approved_users_id.touched ? fields.approved_users_id.error : null
          "
          @blur="fields.approved_users_id.touched = true"
        />
      </div>
      <TabComponent :tabs="[
            { name: 'items', label: 'Items', visible: true },
            {
              name: 'accounting',
              label: 'Accounting',
              visible: isReadOnly && formData.status == 'Approved',
            },
          ]"
          v-model="activeTab">
        <template #items>
        <GridInput label="Items" :error="fields.items.touched ? fields.items.error : null">
          <ItemStockGrid
          ref="itemGridRef"
          :items="formData.items"
          :currency="formData.currency"
          :exchange_rate="formData.exchange_rate"
          :readonly="isReadOnly"
          :ignore-batch-serial="true"
          :warehouses_id="formData.warehouses_id"
          :allow-change-price="false"
          :allow-discount="true"
          :allow-new-row="allowNewItemRow"
          :shelf-managed="warehouse?.shelf_managed ?? false"
          :shelfs="warehouse?.shelfs"
          @items="updateItems"
        />
        </GridInput>
        </template>
        <template #accounting>
          <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
        </template>
      </TabComponent>
      
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
    </template>
  </ModalFormComponent>
</template>
